import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { Clock } from '../../runtime/clock/clock';
import { ConfigService } from '../../runtime/config/config.service';
import { SignageRuntimeService } from '../../runtime/signage-runtime.service';
import { LogoutSurface, MeasurementSystemType } from '../../shared/enums';
import { FooterWidgetComponent } from '../footer-widget/footer-widget.component';

const LOGOUT_HOLD_MS = 10_000;

@Component({
  selector: 'ds-signage-footer',
  imports: [FooterWidgetComponent],
  templateUrl: './signage-footer.component.html',
  styleUrls: ['./signage-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignageFooterComponent {
  private readonly clock = inject(Clock);
  private readonly router = inject(Router);
  private readonly runtime = inject(SignageRuntimeService);
  private readonly configuration = inject(ConfigService).configuration;
  private readonly now = signal(this.clock.now().toEpochMilli());
  private readonly culture = computed(() => this.configuration()?.community.culture ?? 'en-US');
  private readonly timezone = computed(() => this.configuration()?.community.timezone ?? 'UTC');
  private readonly dateFormatter = computed(() => new Intl.DateTimeFormat(this.culture(), {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: this.timezone()
  }));
  private readonly timeFormatter = computed(() => new Intl.DateTimeFormat(this.culture(), {
    hour: 'numeric', minute: 'numeric', timeZone: this.timezone()
  }));
  private logoutHold?: AbortController;

  public readonly weather = this.runtime.weather;
  public readonly mailFeed = this.runtime.mail;
  public readonly temperatureUnit = computed(() =>
    this.weather()?.measurementSystemType === MeasurementSystemType.Metric ? '°C' : '°F');
  public readonly weatherTitle = computed(() => `${this.weather()?.weatherInformation.temperature} ${this.temperatureUnit()}`);
  public readonly weatherDescription = computed(() => this.weather()?.weatherInformation.weather[0].longDescription);
  public readonly weatherIcon = computed(() => `climacon ${this.weather()?.weatherInformation.weather[0].iconName}`);
  public readonly mailVisibility = computed(() => this.mailFeed()?.hasMailArrived ? 'visible' : 'hidden');
  public readonly mailArrivedBuildings = computed(() => {
    const locations = this.mailFeed()?.locations;

    return locations?.length > 1
      ? locations.filter(location => location.hasMailArrived).map(location => location.label).join(', ') : null;
  });
  public readonly communityLogo = computed(() => this.configuration()?.raw.iconLarge);
  public readonly time = computed(() => this.timeFormatter().format(this.now()));
  public readonly date = computed(() => this.dateFormatter().format(this.now()));

  public constructor() {
    const ticker = this.clock.every(1000, 0, () => this.now.set(this.clock.now().toEpochMilli()));
    inject(DestroyRef).onDestroy(() => {
      ticker.dispose();
      this.logoutHold?.abort();
    });
  }

  public startLoggingOut(): void {
    this.stopLoggingOut();
    const hold = new AbortController();
    this.logoutHold = hold;
    void this.clock.after(LOGOUT_HOLD_MS, hold.signal).then((): void => {
      void this.router.navigate(['/logout'], { queryParams: { surface: LogoutSurface.LogoHold } });
    }, (): void => undefined);
  }

  public stopLoggingOut(): void {
    this.logoutHold?.abort();
    this.logoutHold = undefined;
  }
}
